import React, { Fragment, useEffect, useState } from 'react';
import { Box, Text, Input, Flex, Spinner, Checkbox, Button } from '@chakra-ui/react';
import { ChevronDownThickIcon } from '@assets/icons';
import { initialState } from './const';
import { ConditionalComponent, IterationComponent } from '@components/functionComponent';
import OutsideClickHandler from 'react-outside-click-handler';
import { find, get } from 'lodash';
import { CloseIcon } from '@chakra-ui/icons';
import { debounce } from '@helpers/utils';
import './styles.scss';

const Select = ({
    onFetch,
    onSearch,
    options,
    isMultiple,
    searchQuery,
    isCanSearch,
    label,
    isLoading,
    onSelect,
    placeholder,
    isRequired,
    value,
}) => {
    const [state, setState] = useState(initialState);
    const { isOpen, selectedFilter } = state;
    const [searchText, setSearchText] = useState('');

    const onChangeState = (newState) => setState({ ...state, ...newState });

    const onSelectOption = ({ selectedFilter, item, isMultiple }) => {
        const isExist = get(find(selectedFilter, { name: item?.name }), 'name');
        let newSelected = [];
        if (isExist) {
            newSelected = selectedFilter?.filter((option) => option?.name !== item?.name);
            onSelect(newSelected);
            onChangeState({ selectedFilter: newSelected, isOpen: isMultiple });
        } else {
            const newSelected = isMultiple ? [...selectedFilter, item] : [item];
            onSelect(newSelected);
            onChangeState({ selectedFilter: newSelected, isOpen: isMultiple });
        }
    };

    const onSearchHandler = debounce((query) => onSearch(query));

    const onClearSelection = (event) => {
        event.stopPropagation();
        setSearchText('');
        onSearch?.('');
        onSelect([]);
        onChangeState({ selectedFilter: [], isOpen: false });
    };

    useEffect(() => {
        if (isOpen) {
            onFetch({ searchQuery });
            onChangeState({ selectedFilter: value });
        }
        // eslint-disable-next-line
    }, [searchQuery, isOpen]);

    const displayedValue =
        selectedFilter?.length > 1 ? `${selectedFilter?.length} Terpilih` : selectedFilter?.[0]?.name || '';

    return (
        <Fragment>
            <Text className="label">{label}</Text>
            <OutsideClickHandler
                onOutsideClick={() => {
                    if (isOpen) {
                        onChangeState({ isOpen: false });
                        setSearchText('');
                        onSearch?.('');
                    }
                }}
            >
                <Box className="select-form__container">
                    {/* //====================== FORM BUTTON =========================== */}

                    <Box
                        className={`select-button ${isRequired && 'field-required__button'}`}
                        onClick={() => {
                            if (!isOpen) {
                                setSearchText('');
                                onSearch?.('');
                            }
                            onChangeState({ isOpen: true });
                        }}
                        data-testid="select-button__global"
                    >
                        <Input
                            data-testid="select-input__global"
                            placeholder={placeholder}
                            value={isOpen ? searchText : displayedValue}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                onSearchHandler(e.target.value);
                            }}
                            readOnly={!isCanSearch || !isOpen}
                            cursor={isCanSearch && isOpen ? 'caret' : 'pointer !important'}
                        />
                        <Box className="icon-select">
                            <ConditionalComponent
                                statement={!isLoading}
                                fallback={<Spinner color="var(--main-color)" size="sm" />}
                            >
                                <ConditionalComponent
                                    statement={selectedFilter?.length > 0}
                                    fallback={<ChevronDownThickIcon color="grey" className="icon-container" />}
                                >
                                    <Button variant="unstyled" onClick={onClearSelection}>
                                        <CloseIcon color="grey" className="icon-container" boxSize="12px" />
                                    </Button>
                                </ConditionalComponent>
                            </ConditionalComponent>
                        </Box>
                    </Box>

                    {/* //====================== REQUIRED TEXT =========================== */}

                    <ConditionalComponent statement={isRequired}>
                        <Text className="field-required__text">Field is required!</Text>
                    </ConditionalComponent>

                    {/* //====================== FORM LIST =========================== */}
                    <ConditionalComponent statement={isOpen}>
                        <Box className="form-list">
                            <ConditionalComponent
                                statement={options?.length}
                                fallback={<Text className="not-found__text">Not Found</Text>}
                            >
                                <IterationComponent
                                    arr={options}
                                    render={(item) => {
                                        const isExist = get(find(selectedFilter, { name: item?.name }), 'name');
                                        return (
                                            <Flex
                                                key={item?.id}
                                                className={`select-item ${isExist && 'select-item__active'}`}
                                                onClick={() => onSelectOption({ item, selectedFilter, isMultiple })}
                                            >
                                                <ConditionalComponent
                                                    statement={isMultiple}
                                                    fallback={<Text className="select-text">{item?.name}</Text>}
                                                >
                                                    <Flex gap={2} alignItems="center">
                                                        <Checkbox isChecked={!!isExist} pointerEvents="none" />
                                                        <Text className="select-text">{item?.name}</Text>
                                                    </Flex>
                                                </ConditionalComponent>
                                                <Box className="circle-active" />
                                            </Flex>
                                        );
                                    }}
                                />
                            </ConditionalComponent>
                        </Box>
                    </ConditionalComponent>
                </Box>
            </OutsideClickHandler>
        </Fragment>
    );
};

export default Select;
